/*
 * Rules workspace — global editor + confusion rule selection shared by all
 * features. Ported from renderRulesWorkspace / renderRules / renderRulePresets /
 * renderConfusionRulesSummary in taskpane.js. Selection logic lives in
 * rules/ruleLogic.ts; this component renders it with Fluent controls.
 */

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  MessageBar,
  MessageBarBody,
  Select,
  Subtitle2,
  Text,
  Tooltip,
  makeStyles,
  tokens,
  mergeClasses,
  type SelectOnChangeData,
} from "@fluentui/react-components";
import { useState, useEffect, type ChangeEvent } from "react";
import { ChevronIcon } from "../icons/ChevronIcon";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import {
  fetchCustomRulePresets,
  createGreekEditorRulePreset,
  splitManagedPresets,
  buildForkPayload,
  type CustomRulePreset,
} from "../../api/client";
import {
  deselectRuleAndDependents,
  getBatchRules,
  getBuiltInPresets,
  getCatalogPreferenceDisplay,
  getCatalogSectionDisplay,
  getConfusionRules,
  getFriendlyRule,
  getPreferenceOptions,
  humanizeRuleId,
  resolveRuleSelection,
  selectRuleWithDependencies,
  buildKnownPresetSignatures,
} from "../../rules/ruleLogic";
import type { Rule, PreferenceValues } from "../../state/types";
import { classifyRuleRecency, ruleRecencyTimestamp } from "../../rules/ruleRecency";
import { fetchRecentRuleSets } from "../../api/client";
import type { RecentRuleSet } from "../../state/types";

const useStyles = makeStyles({
  panel: { display: "flex", flexDirection: "column", gap: "16px" },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  headerActions: { display: "flex", gap: "8px" },
  presets: { display: "flex", flexWrap: "wrap", gap: "8px" },
  presetCard: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px 10px",
    cursor: "pointer",
    minWidth: "140px",
  },
  presetSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderTop: `2px solid ${tokens.colorBrandStroke1}`,
    borderRight: `2px solid ${tokens.colorBrandStroke1}`,
    borderBottom: `2px solid ${tokens.colorBrandStroke1}`,
    borderLeft: `2px solid ${tokens.colorBrandStroke1}`,
    boxShadow: tokens.shadow4,
  },
  sectionHeader: {
    fontWeight: tokens.fontWeightSemibold,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: "3px",
    marginTop: "8px",
  },
  ruleItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "6px 0",
  },
  ruleDesc: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  badges: { display: "flex", flexWrap: "wrap", gap: "4px" },
  preference: { display: "flex", flexDirection: "column", gap: "2px", marginTop: "4px" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  confusionList: { display: "flex", flexDirection: "column", gap: "6px" },
  confusionCard: { display: "flex", flexDirection: "column", gap: "2px", padding: "8px 10px" },
});

export function RulesPanel(): JSX.Element {
  const styles = useStyles();
  const { t, lang } = useTranslation();
  const patch = useStore((s) => s.patch);
  const rules = useStore((s) => s.rules);
  const catalogPreferences = useStore((s) => s.catalogPreferences);
  const catalogSections = useStore((s) => s.catalogSections);
  const preferences = useStore((s) => s.preferences);
  const selectedRuleIds = useStore((s) => s.selectedRuleIds);
  const selectedConfusionRuleIds = useStore((s) => s.selectedConfusionRuleIds);
  const selectedPresetId = useStore((s) => s.selectedPresetId);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [confusionExpanded, setConfusionExpanded] = useState(false);
  const [customPresets, setCustomPresets] = useState<CustomRulePreset[]>([]);
  const [recentRuleSets, setRecentRuleSets] = useState<RecentRuleSet[]>([]);
  const [forkingId, setForkingId] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await fetchRecentRuleSets();
        if (active) setRecentRuleSets(list);
      } catch {
        // ignore
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await fetchCustomRulePresets();
        if (active) setCustomPresets(list);
      } catch {
        // ignore fetch failures
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const batchRules = getBatchRules(rules);
  const confusionRules = getConfusionRules(rules);
  const builtIn = getBuiltInPresets(rules, lang);
  const now = new Date();
  const recencyById = new Map(
    rules
      .map((rule) => [rule.id, classifyRuleRecency(rule, now)] as const)
      .filter(([, r]) => r.isNew || r.isUpdated),
  );
  const recentlyChangedRules = [...recencyById.keys()]
    .map((id) => rules.find((r) => r.id === id))
    .filter((r): r is Rule => Boolean(r))
    .sort((a, b) => ruleRecencyTimestamp(b) - ruleRecencyTimestamp(a));

  const knownSignatures = buildKnownPresetSignatures(rules, lang, customPresets, preferences);
  const visibleRecent = recentRuleSets.filter((item) => !knownSignatures.has(item.signature));

  const grouped: Record<string, Rule[]> = {};
  batchRules.forEach((rule) => {
    const sec = rule.section || "orthography";
    (grouped[sec] = grouped[sec] || []).push(rule);
  });

  const selectAll = () =>
    patch({
      selectedRuleIds: resolveRuleSelection(
        rules,
        batchRules.map((r) => r.id)
      ),
      selectedPresetId: "",
      rulesConfigured: true,
    });

  const deselectAll = () =>
    patch({ selectedRuleIds: [], selectedPresetId: "", rulesConfigured: true });

  const toggleRule = (ruleId: string, checked: boolean) =>
    patch({
      selectedRuleIds: checked
        ? selectRuleWithDependencies(rules, selectedRuleIds, ruleId)
        : deselectRuleAndDependents(rules, selectedRuleIds, ruleId),
      selectedPresetId: "",
      rulesConfigured: true,
    });

  const changePreference = (key: string, value: string) =>
    patch({ preferences: { ...preferences, [key]: value }, selectedPresetId: "", rulesConfigured: true });

  const splitPresets = splitManagedPresets(customPresets);

  const handleDuplicate = async (preset: CustomRulePreset) => {
    setForkingId(preset.presetId);
    try {
      await createGreekEditorRulePreset(buildForkPayload(preset));
      const list = await fetchCustomRulePresets();
      setCustomPresets(list);
    } catch {
      // ignore
    } finally {
      setForkingId("");
    }
  };

  const applyPreset = (presetId: string, ruleIds: string[], presetPreferences?: PreferenceValues) => {
    const nextPrefs = presetPreferences ? { ...preferences, ...presetPreferences } : preferences;
    patch({
      selectedPresetId: presetId,
      selectedRuleIds: resolveRuleSelection(rules, ruleIds),
      preferences: nextPrefs,
      rulesConfigured: true,
    });
  };

  const toggleConfusion = (ruleId: string, checked: boolean) =>
    patch({
      selectedConfusionRuleIds: checked
        ? [...new Set([...selectedConfusionRuleIds, ruleId])]
        : selectedConfusionRuleIds.filter((id) => id !== ruleId),
      rulesConfigured: true,
    });

  return (
    <div className="panel-section">
      <MessageBar intent="info" className="status-card">
        <MessageBarBody>{t.rulesInfo}</MessageBarBody>
      </MessageBar>
      <div className={styles.headerRow}>
        <Subtitle2>{t.rulesTitle}</Subtitle2>
        <div className={styles.headerActions}>
          <Button size="small" appearance="secondary" onClick={selectAll}>
            {t.selectAll}
          </Button>
          <Button size="small" appearance="subtle" onClick={deselectAll}>
            {t.deselectAll}
          </Button>
        </div>
      </div>

      {/* Recently changed rules */}
      {recentlyChangedRules.length > 0 && (
        <div>
          <div
            className={styles.sectionHeader}
            onClick={() => setRecentExpanded(!recentExpanded)}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", userSelect: "none" }}
          >
            <ChevronIcon expanded={recentExpanded} />
            <span>{t.recentlyChangedTitle}</span>
          </div>
          {recentExpanded && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
              {recentlyChangedRules.map((rule) => {
                const friendly = getFriendlyRule(rule, rules, catalogSections, lang);
                const r = recencyById.get(rule.id)!;
                return (
                  <div key={`recent-${rule.id}`} className={styles.ruleItem}>
                    <div className={styles.badges}>
                      <Badge appearance="filled" color={r.isNew ? "success" : "informative"} size="small">
                        {r.isNew ? t.badgeNew : t.badgeUpdated}
                      </Badge>
                      <Text weight="semibold">{friendly.name}</Text>
                    </div>
                    {friendly.desc ? <span className={styles.ruleDesc}>{friendly.desc}</span> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Built-in presets */}
      <div>
        <Text weight="semibold">{t.rulePresetsTitle}</Text>
        <div className={styles.hint}>{t.rulePresetsHint}</div>
        <div className={styles.presets}>
          {builtIn.map((preset) => (
            <Tooltip key={preset.id} content={preset.description} relationship="description">
              <Card
                className={mergeClasses(
                  styles.presetCard,
                  selectedPresetId === preset.id && styles.presetSelected
                )}
                onClick={() => applyPreset(preset.id, preset.ruleIds)}
              >
                <Text weight="semibold">{preset.title}</Text>
                <Text className={styles.ruleDesc}>
                  {t.selectedRulesCount.replace("{count}", String(preset.ruleIds.length))}
                </Text>
              </Card>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Managed presets section */}
      {splitPresets.managed.length > 0 && (
        <div>
          <Text weight="semibold">{t.managedSectionTitle}</Text>
          <div className={styles.presets}>
            {splitPresets.managed.map((preset) => (
              <Card key={preset.presetId} className={styles.presetCard}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Text weight="semibold">{preset.alias}</Text>
                  <Badge appearance="filled" color="brand" size="small">
                    {t.managedBadge}
                  </Badge>
                </div>
                <Text className={styles.ruleDesc}>
                  {t.selectedRulesCount.replace("{count}", String(preset.ruleIds.length))}
                </Text>
                <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                  <Button
                    size="small"
                    appearance="secondary"
                    onClick={() => applyPreset(preset.presetId, preset.ruleIds, preset.preferences)}
                  >
                    {t.selectAll}
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={forkingId === preset.presetId}
                    onClick={() => handleDuplicate(preset)}
                  >
                    {forkingId === preset.presetId ? t.duplicatingPreset : t.duplicatePreset}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Personal presets section */}
      {splitPresets.personal.length > 0 && (
        <div>
          <Text weight="semibold">{t.personalSectionTitle}</Text>
          <div className={styles.presets}>
            {splitPresets.personal.map((preset) => (
              <Tooltip key={preset.presetId} content={preset.description ?? ""} relationship="description">
                <Card
                  className={mergeClasses(
                    styles.presetCard,
                    selectedPresetId === preset.presetId && styles.presetSelected
                  )}
                  onClick={() => applyPreset(preset.presetId, preset.ruleIds, preset.preferences)}
                >
                  <Text weight="semibold">{preset.alias}</Text>
                  <Text className={styles.ruleDesc}>
                    {t.selectedRulesCount.replace("{count}", String(preset.ruleIds.length))}
                  </Text>
                </Card>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      {/* Recent ad-hoc selections */}
      {visibleRecent.length > 0 && (
        <div style={{ marginTop: "8px" }}>
          <Text weight="semibold">{t.recentSelectionsTitle}</Text>
          <div className={styles.presets}>
            {visibleRecent.map((item) => (
              <Card
                key={item.recentId}
                className={styles.presetCard}
                onClick={() =>
                  patch({
                    selectedRuleIds: resolveRuleSelection(rules, item.ruleIds),
                    preferences: { ...preferences, ...(item.preferences || {}) },
                    selectedPresetId: "",
                    rulesConfigured: true,
                  })
                }
              >
                <Text weight="semibold">
                  {t.recentSelectionLabel.replace("{count}", String(item.ruleIds.length))}
                </Text>
                {item.usedAt ? (
                  <Text className={styles.ruleDesc}>{new Date(item.usedAt).toLocaleDateString()}</Text>
                ) : null}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Editor rules grouped by section */}
      {Object.entries(grouped).map(([sectionKey, sectionRules]) => {
        const totalCount = sectionRules.length;
        const selectedCount = sectionRules.filter((rule) => selectedRuleIds.includes(rule.id)).length;
        return (
          <div key={sectionKey}>
            <div
              className={styles.sectionHeader}
              onClick={() => toggleSection(sectionKey)}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", userSelect: "none" }}
            >
              <ChevronIcon expanded={expandedSections[sectionKey]} />
              <span>
                {getCatalogSectionDisplay(catalogSections, sectionKey, lang).title}
                {` (${selectedCount}/${totalCount})`}
              </span>
            </div>
            {expandedSections[sectionKey] && (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
                {sectionRules.map((rule) => {
                  const friendly = getFriendlyRule(rule, rules, catalogSections, lang);
                  const checked = selectedRuleIds.includes(rule.id);
                  const dependencyNames = (rule.dependsOn || []).map(
                    (depId) => getFriendlyRule(depId, rules, catalogSections, lang).name
                  );
                  const prefKey = rule.preferenceKey;
                  const prefOptions = prefKey ? getPreferenceOptions(catalogPreferences, prefKey) : [];
                  const prefDisplay = prefKey
                    ? getCatalogPreferenceDisplay(catalogPreferences, prefKey, lang)
                    : null;
                  const prefValue = prefKey ? preferences[prefKey] || prefOptions[0] || "" : "";

                  return (
                    <div key={rule.id} className={styles.ruleItem}>
                      <Checkbox
                        checked={checked}
                        onChange={(_e, data) => toggleRule(rule.id, Boolean(data.checked))}
                        label={friendly.name}
                      />
                      {friendly.desc ? <span className={styles.ruleDesc}>{friendly.desc}</span> : null}
                      <div className={styles.badges}>
                        {recencyById.get(rule.id) ? (
                          <Badge appearance="tint" color={recencyById.get(rule.id)!.isNew ? "success" : "informative"} size="small">
                            {recencyById.get(rule.id)!.isNew ? t.badgeNew : t.badgeUpdated}
                          </Badge>
                        ) : null}
                        {rule.isSafe === false ? (
                          <Badge appearance="tint" color="warning" size="small">
                            {t.unsafeRuleLabel}
                          </Badge>
                        ) : null}
                        {dependencyNames.length ? (
                          <Badge appearance="outline" size="small">
                            {`${t.dependsOn}: ${dependencyNames.join(", ")}`}
                          </Badge>
                        ) : null}
                      </div>
                      {prefKey && prefOptions.length ? (
                        <label className={styles.preference}>
                          <span className={styles.ruleDesc}>{prefDisplay?.label}</span>
                          <Select
                            value={prefValue}
                            onChange={(_e: ChangeEvent<HTMLSelectElement>, data: SelectOnChangeData) =>
                              changePreference(prefKey, data.value)
                            }
                          >
                            {prefOptions.map((option) => (
                              <option key={option} value={option}>
                                {prefDisplay?.options?.[option] || humanizeRuleId(option, lang)}
                              </option>
                            ))}
                          </Select>
                        </label>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Confusion rules */}
      <Divider />
      <div>
        <div
          className={styles.sectionHeader}
          onClick={() => setConfusionExpanded(!confusionExpanded)}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", userSelect: "none" }}
        >
          <ChevronIcon expanded={confusionExpanded} />
          <span>
            {t.confusionRulesTitle}
            {` (${selectedConfusionRuleIds.length}/${confusionRules.length})`}
          </span>
        </div>
        
        {confusionExpanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
            <div className={styles.hint}>{t.confusionRulesHint}</div>
            {confusionRules.length === 0 ? (
              <Text className={styles.hint}>{t.noConfusionRulesAvailable}</Text>
            ) : (
              <div className={styles.confusionList}>
                {confusionRules.map((rule) => {
                  const friendly = getFriendlyRule(rule, rules, catalogSections, lang);
                  const active = selectedConfusionRuleIds.includes(rule.id);
                  return (
                    <Card key={rule.id} className={styles.confusionCard}>
                      <Checkbox
                        checked={active}
                        onChange={(_e, data) => toggleConfusion(rule.id, Boolean(data.checked))}
                        label={`${friendly.name} · ${active ? t.enabledRule : t.disabledRule}`}
                      />
                      <span className={styles.ruleDesc}>{friendly.desc}</span>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
